import { OUTCOMES, type Channel, type InquiryType, type Outcome } from "./types";
import { SCENARIO_MAP } from "./scenario";
import { realizedRevenue, type OutcomeAmountStatus, type RealizedRevenue } from "./revenue";
import { DAY_MS } from "./window";

/**
 * Loop item L7, `pnpm measure:sales`: whether the agents move sales, as far
 * as the rows can say. Pure function, no IO, the split campaignRoi.ts and
 * outcomeDigest.ts already use: this takes rows the loader read
 * (`packages/db/src/salesEvidence.ts`) and does every piece of arithmetic in
 * deterministic code (CLAUDE.md section 4, Agent Brains 5.7's L4
 * "zero-tolerance numbers").
 *
 * ASSOCIATION, NOT LIFT. There is no control group: every thread either had
 * an agent draft transmitted on it or did not, and nothing chose which at
 * random. "Assisted" below says an agent-composed reply left the building
 * before the outcome and inside its window, and nothing more. Agent Brains
 * 5.7's causal hygiene ("never reports correlation as cause") is why no
 * field here is called lift, uplift or effect.
 *
 * F-06. A first response is a message that actually left the building: a
 * `messages` row from the shop (the owner typing in the Meta inbox) or the
 * agent (transmit.ts, on a definite transmit). A draft released and never
 * transmitted wrote no message, so it is no response, and it is counted
 * apart so the gap shows.
 *
 * H13, ENTRY ATTRIBUTION. A section of this same measure, not a second verb:
 * the new inquiries and the outcomes recorded in the period, per entry
 * source (the story or post id a thread's FIRST customer message carried;
 * Inbox Composer section 6, "this reel produced 14 conversations and 2
 * deposits"). An entry source is where a thread STARTED. It is not what
 * caused a sale, it is no ranking and it carries no weight: whether it feeds
 * the call list is Kabir's to decide, and the call list reads nothing here.
 *
 * WHAT THE FIRST MESSAGE CARRIED, NOT WHAT THE THREAD HOLDS. resolveThread
 * keeps the first non-null `threads.entry_source`, so a thread that began as
 * a plain DM and later got a story reply holds that story's id. Crediting the
 * story with the inquiry (and with a deposit that came before the reply)
 * would be the causal-hygiene error this section exists to prevent (Agent
 * Brains 5.7), and a past period's report would change on every re-run. So
 * the credit goes to the id the first customer message's own record carried
 * (`entrySource`), and a thread whose first message carried none but which
 * holds one now (`threadEntrySource`) is counted on its own line, not
 * credited. No code records an ad id yet (the webhook parser reads none), so
 * a thread an ad started has no source of its own: it is in the no-source line,
 * unless the same person commented on a reel shortly before it (sheet item
 * 159), and then the reel is credited, a sequence and not a cause.
 *
 * No text is read here. The loader reads one text, the draft text that
 * left (what transmit.ts sends), and only to hand core's `carriesFixedAsk`
 * (decision list item 99b) the question of whether it held the fixed
 * contact ask: the answer is a boolean and the text goes no further. No
 * message text is read anywhere (CLAUDE.md section 10).
 */

/**
 * The attribution window per inquiry type, from the Auto-Learn System Spec
 * section 3: "Full-trajectory attribution: the whole thread gets the outcome,
 * windows per inquiry type (repair ~7 days, custom/engagement ~90 days)".
 * RATIFIED by Kabir 2026-10-02 (decision list item 140, typed reply "Yes I want
 * to continue the production" to the accept-the-recs question): repair 7 days
 * and custom 90 days exactly. The vault writes both with "~"; the ruling takes
 * them as written. By the ruling both edges are exclusive: an outcome counts
 * when it was recorded strictly after the transmit and strictly less than the
 * window after it. The window is anchored at the latest agent-composed
 * transmit on the thread before the outcome, and a holding line is not
 * composed.
 * "custom/engagement" is read as the `custom` inquiry type (INQUIRY_TYPES has no
 * other). Every other inquiry type is unwindowed: it has no window here, is
 * never given one, and its outcomes are counted as unwindowed.
 */
export const SALES_ATTRIBUTION_WINDOW_MS: Readonly<Partial<Record<InquiryType, number>>> = {
  repair: 7 * DAY_MS,
  custom: 90 * DAY_MS,
};

/**
 * Sheet item 146 (accepted 2026-10-02): the inquiry type a routed scenario
 * names, for outcomes nobody typed a type on. T2 content.
 * AWAITING RATIFICATION: the four pairs below are the implementer's reading
 * of which scenarios name a type, and so is the lookback beside them. Kabir
 * accepted the item that builds the map, and ratifies its content later.
 * It is applied at report time and never stored: nothing writes it to
 * `outcomes.inquiry_type`, so removing or changing a pair changes the next
 * report and no row. An owner-typed type (`pnpm outcomes record
 * --inquiry-type`) is stored, wins over this map, and is reported as
 * owner-stated; a type read through here is reported as from the map.
 *
 * Four scenarios name a type. price_check is NOT mapped from `price_challenge`
 * (a price question reads as a ready-made purchase or as a challenge to a
 * quote), and dead is how a thread ended, not what it asked, so neither
 * inquiry type is named by any scenario. Every other scenario is a conversation
 * stage or a class (consult, review ask, vendor spam, fraud) and names no type.
 * Only routings inside `SALES_ROUTING_LOOKBACK_MS` before the outcome are read.
 */
export const SALES_INQUIRY_TYPE_OF_SCENARIO: Readonly<Record<string, InquiryType>> = {
  repair_inquiry: "repair",
  custom_engagement: "custom",
  ready_made_purchase: "ready_made",
  complaint_warranty: "warranty",
};

/**
 * Sheet item 146, review finding: how far before an outcome the scenario map
 * reads the scenarios routed on its thread. A thread is permanent per contact
 * and channel, so a conversation from months before would otherwise name the
 * type of today's sale and move it into a window it has no business in.
 * AWAITING RATIFICATION: the longest attribution window (90 days) is the
 * implementer's reading of how far back a routing can still say what an
 * outcome was about. It is written out so the ratification sheet shows the
 * value, and a test pins it equal to the longest of
 * `SALES_ATTRIBUTION_WINDOW_MS`, so a ruling that moves a window cannot leave
 * this behind unseen. Its old edge is exclusive like the windows: a routing
 * exactly this long before the outcome is not read. A routing after the
 * outcome is never read. Kabir sets the bound together with the map.
 */
export const SALES_ROUTING_LOOKBACK_MS: number = 90 * DAY_MS;

/**
 * Sheet item 159 (accepted 2026-10-02): the comment surface that pairs with a
 * DM surface. A thread filed under one of these DM channels may be the later
 * half of a lead that began as a comment on the same platform; no other channel
 * has a comment surface to look for. The pairing is the ingest parser's
 * (`COMMENT_CHANNEL` in packages/pipeline webhook/parse.ts), restated here
 * because core imports nothing from the pipeline, and pinned against the real
 * parser in `pg/measureSalesEntry.integration.test.ts`.
 */
export const SALES_COMMENT_CHANNEL_OF: Readonly<Partial<Record<Channel, Channel>>> = {
  instagram: "instagram_comment",
  facebook: "facebook_comment",
};

/**
 * Sheet item 159, review finding: how far before a DM thread's first customer
 * message a comment may be and still speak for it. Meta's private-reply window,
 * `PRIVATE_REPLY_WINDOW_MS` (7 days, commentReply.ts, read from Meta's docs and
 * ruled in TA-414 B5): the span in which the shop can answer a comment in DM.
 * AWAITING RATIFICATION: using that window as the attribution lookback is the
 * implementer's reading, because the sheet item says nothing on how recent the
 * comment must be. It is written out so the ratification sheet shows the
 * value, and a test pins it equal to `PRIVATE_REPLY_WINDOW_MS`. A comment
 * thread is permanent per contact and surface, so without a bound a DM would
 * be credited to a reel the person commented on months before. Past the bound
 * the DM is held back under its own reason, never credited. Both edges are
 * exclusive, as the attribution windows are: a comment exactly this long
 * before the DM's first message does not speak for it.
 */
export const SALES_COMMENT_LOOKBACK_MS: number = 7 * DAY_MS;

/** A contact's comment thread, as far as entry attribution reads it. */
export interface SalesContactCommentThread {
  /**
   * The contact's LATEST customer comment on that comment thread strictly
   * before the DM thread's first customer message, or null when no comment
   * came before it. The thread is permanent per contact and surface, so every
   * comment the person ever made on the platform lives on it: the loader hands
   * over the one nearest the DM, never the first the person ever made, which
   * would credit the DM to whatever post they happened to comment on first.
   * Core decides whether it is close enough (`SALES_COMMENT_LOOKBACK_MS`).
   */
  readonly precedingComment: {
    /** When that comment was sent. */
    readonly at: Date;
    /**
     * The media id that comment itself carried, read from its own
     * `message.received` record, as `SalesInquiryRow.entrySource` is. Null
     * when it carried none.
     */
    readonly entrySource: string | null;
  } | null;
}

/** One thread whose first customer message, ever, falls in the period. */
export interface SalesInquiryRow {
  readonly threadId: string;
  readonly channel: string;
  readonly firstCustomerAt: Date;
  /** `routing.decided`'s scenario for that first message. Null when it was never routed. */
  readonly scenario: string | null;
  /**
   * H13: the story or post id the thread's FIRST customer message itself
   * carried, read from that message's own `message.received` record. A
   * platform media id, not a name and not customer data. Null when that
   * message carried none (or has no record), which says the platform named
   * none and nothing about where it came from.
   */
  readonly entrySource: string | null;
  /**
   * H13: `threads.entry_source` as it stands when the report runs. It is the
   * first non-null id ANY message carried, so it can come from a message
   * after the first one. Used only to tell a thread that learned a source
   * later from one that never had any; never credited to a source.
   */
  readonly threadEntrySource: string | null;
  /**
   * Sheet item 159: the same contact's comment thread on this thread's own
   * surface (Instagram DM with Instagram comments, Facebook with Facebook), or
   * null when the contact has none, or this channel has no comment surface.
   * Its `precedingComment` is the latest comment before this thread began.
   * The loader joins on the contact row `resolveThread` files both threads
   * under, so a comment and a DM that Meta delivered under different sender ids
   * are two contacts and never meet here. Threads are unique per contact and
   * channel, so there is at most one.
   */
  readonly contactCommentThread: SalesContactCommentThread | null;
  /**
   * The first shop or agent message at or after the first customer message.
   * `lastCustomerAt` is the latest customer message at or before it, which
   * is where stage 1 measures the corpus's first response from.
   */
  readonly firstResponse: {
    readonly at: Date;
    readonly by: "shop" | "agent";
    readonly lastCustomerAt: Date;
  } | null;
  /** A draft on the thread was released (approved or sent) and never transmitted. */
  readonly releasedNotTransmitted: boolean;
}

/**
 * One draft transmitted in the period, for the contact-ask share. Whether it
 * carried the fixed ask is read off the text that left, because the flags
 * alone cannot say so: compose stores `contact_ask_appended = false` both
 * when the reply had no ask and when the model's own reply already held the
 * fixed line (compose's "asked", item 99b), and an owner can edit an appended
 * ask out at approval. The flags say how an ask that left got there.
 */
export interface SalesAskRow extends SalesTransmitRow {
  /** Core's `carriesFixedAsk` on `approved_body` when set, else `body`: the text transmit.ts sends. */
  readonly carriesFixedAsk: boolean;
}

/** One customer message in the period, with its routed scenario or null. */
export interface SalesRoutedMessageRow {
  readonly scenario: string | null;
}

/**
 * Sheet item 146: one customer message on a thread that holds an outcome with
 * no typed inquiry type, with the scenario routed for it. Read to apply the
 * scenario map at report time. Never a word of the message.
 */
export interface SalesRoutingRow {
  readonly threadId: string;
  readonly sentAt: Date;
  /** `routing.decided`'s scenario for that message. Null when it was never routed. */
  readonly scenario: string | null;
}

/** One draft that left the building (`drafts.transmitted_at` set). */
export interface SalesTransmitRow {
  readonly draftId: string;
  readonly threadId: string;
  readonly transmittedAt: Date;
  /** TA-467: true is the escalation's fixed line, which no model wrote. NULL reads as composed. */
  readonly holdingLine: boolean | null;
  /** TA-499 (migration 0035): code appended the fixed ask at compose. NULL is not recorded. */
  readonly contactAskAppended: boolean | null;
  /** Decision list item 105: the `draft.approved` event says the ask was added at approval. */
  readonly contactAskAddedAtApproval: boolean;
}

export interface SalesOutcomeRow {
  readonly outcomeId: string;
  readonly threadId: string;
  readonly label: Outcome;
  /**
   * The type the owner typed (`pnpm outcomes record --inquiry-type`), or null
   * when none was. The ghost sweep and a chip tap write none, so a null is the
   * usual row, and the scenario map may name one at report time.
   */
  readonly inquiryType: string | null;
  readonly amountCents: number | null;
  /** `stated` | `unknown` on a sold or deposit row (migration 0060), null on a label that carries no amount. */
  readonly amountStatus: OutcomeAmountStatus | null;
  readonly recordedAt: Date;
  readonly reversedAt: Date | null;
}

export interface SalesInput {
  readonly inquiries: readonly SalesInquiryRow[];
  readonly routedMessages: readonly SalesRoutedMessageRow[];
  /** Drafts transmitted in the period, for the contact-ask share. */
  readonly transmittedInPeriod: readonly SalesAskRow[];
  /** Shop messages in the period: owner-typed replies, which carry no ask flag. */
  readonly ownerRepliesInPeriod: number;
  /** Outcomes recorded in the period, reversed ones included (they are counted apart). */
  readonly outcomes: readonly SalesOutcomeRow[];
  /** Every transmitted draft on the outcomes' threads, whenever it left. */
  readonly transmits: readonly SalesTransmitRow[];
  /**
   * Customer messages on the threads of outcomes with no typed inquiry type,
   * with their routed scenario. Empty is legitimate: nothing then names a type.
   */
  readonly outcomeRouting: readonly SalesRoutingRow[];
}

export interface SalesCount {
  readonly key: string;
  readonly count: number;
}

/** Milliseconds. Null when there is no sample: no time is not zero time. */
export interface SalesSpread {
  readonly count: number;
  readonly medianMs: number | null;
  readonly p90Ms: number | null;
}

export interface SalesBucket {
  readonly count: number;
  /** Integer cents over the rows that carry an amount, this label only. */
  readonly amountCents: number;
  readonly withoutAmount: number;
}

export interface SalesLabelTally extends SalesBucket {
  readonly assisted: SalesBucket;
  readonly ownerOnly: SalesBucket;
  readonly unwindowed: SalesBucket;
  /**
   * Rows with an inquiry type that has a window, the only rows the assisted
   * vs owner-only split can be measured on. At 0 the split did not run on
   * this label, which is not the same as 0 assisted and 0 owner-only.
   */
  readonly windowed: number;
  /**
   * Unwindowed rows of this label that have no inquiry type at all: none typed
   * by the owner and none the map names. Equals `inquiryTypeBasis.ambiguous`
   * plus `inquiryTypeBasis.noMappedScenario`.
   */
  readonly inquiryTypeNotRecorded: number;
  /** Where the inquiry types behind this label's rows came from (sheet item 146). */
  readonly inquiryTypeBasis: SalesInquiryTypeBasis;
}

/**
 * Sheet item 146. Every unreversed row of a label is in exactly one place
 * here: its type was typed by the owner, or read through the scenario map, or
 * the map could not name one (the scenarios routed up to the outcome name two
 * different types, or none).
 */
export interface SalesInquiryTypeBasis {
  /** Types the owner typed, most first then by type. */
  readonly ownerStated: readonly SalesCount[];
  /** Types the scenario map named at report time, most first then by type. */
  readonly fromMap: readonly SalesCount[];
  /** Two or more different mapped types before the outcome: not inferred. */
  readonly ambiguous: number;
  /** No routed scenario before the outcome names a type, or none was routed. */
  readonly noMappedScenario: number;
}

/**
 * H13: the most entry sources the report lists. The rest are counted as one
 * overflow line with their outcomes, never silently dropped.
 * AWAITING RATIFICATION: 20 is the implementer's pick, and the cap is Kabir's to set.
 */
export const SALES_ENTRY_SOURCE_CAP = 20;

/**
 * What an entry source must look like to be printed: the platform's own ids
 * are digits and underscores, the fixtures add letters, and nothing else a
 * webhook field could hold belongs on an operator's terminal. One that does
 * not match is counted in `unprintable`, the way an unrecognized scenario is
 * counted and not named.
 */
const ENTRY_SOURCE_ID = /^[A-Za-z0-9_.:-]{1,128}$/;

/** New inquiries that came in on one source (or on none), with the outcomes recorded on those threads. */
export interface SalesEntryTally {
  /** Threads whose first customer message ever falls in the period, section 1's definition. */
  readonly inquiries: number;
  /**
   * Of those, the DM threads credited to this source through the same
   * contact's comment thread (sheet item 159), not through their own first
   * message. Always 0 on the no-source and later-source lines.
   */
  readonly viaContactComment: number;
  /**
   * Outcomes recorded in the period on those same threads, reversed ones
   * excluded. Every label, zero-filled, in OUTCOMES order. Cents are totalled
   * per label and never across labels (TA-504).
   */
  readonly byLabel: Readonly<Record<Outcome, SalesBucket>>;
}

export interface SalesEntrySource extends SalesEntryTally {
  /** The platform's id for the story or post. Never a name. */
  readonly entrySource: string;
}

/**
 * Sheet item 159. A DM thread that began with no source (its first message
 * carried none and the thread holds none) is credited to the media id of the
 * contact's latest comment on the same surface, only when that comment came
 * strictly before the DM thread's first customer message and less than
 * `SALES_COMMENT_LOOKBACK_MS` before it. Each thread is still one inquiry, so
 * one person who commented and then wrote shows as two inquiries under the
 * reel, the second of them named in `credited`. A sequence, not a cause:
 * nothing records that the comment led to the DM. What the join could not do is
 * counted, by reason, on the no-source line.
 */
export interface SalesContactCommentJoin {
  /** DM threads credited to a source through the contact's comment thread. */
  readonly credited: number;
  /** No-source threads that stayed there, by why the comment thread did not speak for them. Sums to `noSource.inquiries`. */
  readonly heldBack: {
    /** A DM thread whose contact has no comment thread on this surface. */
    readonly noCommentThread: number;
    /** The comment thread exists but no comment on it came strictly before the DM thread's first customer message. */
    readonly noCommentBefore: number;
    /** The latest comment before the DM is `SALES_COMMENT_LOOKBACK_MS` or more earlier: too old to speak for it. */
    readonly commentTooOld: number;
    /** The latest comment before the DM, inside the lookback, carried no media id. */
    readonly commentCarriedNone: number;
    /** A thread on a channel with no comment surface (sms, email, webchat), or a comment thread itself. */
    readonly notADmThread: number;
  };
  /** Every thread in every tally of section 4. Equal to section 1's new inquiries, or the measure refuses. */
  readonly accounted: number;
}

export interface SalesEntryAttribution {
  readonly cap: number;
  /** Most inquiries first, then by id in code-unit order. At most `cap` of them. */
  readonly sources: readonly SalesEntrySource[];
  /** Every source past the cap, as one tally. `sources` is how many, 0 when nothing overflowed. */
  readonly overflow: SalesEntryTally & { readonly sources: number };
  /** Threads with no entry source on record, on the thread or on its first message: one line. */
  readonly noSource: SalesEntryTally;
  /**
   * Threads whose first customer message carried no source but whose thread
   * holds one now (a later message named it). Counted so the section
   * reconciles, not credited to that source, and its id goes no further.
   * As of the day the report runs: a story reply tomorrow moves a thread here
   * from `noSource`, and never into a source.
   */
  readonly laterSource: SalesEntryTally;
  /** Threads whose entry source is not a plain platform id. Counted, and the value goes no further. */
  readonly unprintable: SalesEntryTally;
  /**
   * Sheet item 159: how the contact's comment thread and DM thread were
   * counted together, so section 4 can be read against section 1.
   */
  readonly contactComment: SalesContactCommentJoin;
  /** Reversed outcomes on these threads, excluded from every tally here and counted apart. */
  readonly reversed: Readonly<Record<Outcome, number>>;
  /**
   * Unreversed outcomes recorded in the period on threads that are not new
   * inquiries in it: they began earlier, or have no customer message in the
   * period (the owner wrote first and the customer has not answered yet).
   * Section 3 counts them; they belong to no source here, and this keeps the
   * gap between the two visible.
   */
  readonly onOtherThreads: Readonly<Record<Outcome, number>>;
}

export interface SalesMeasure {
  readonly entryAttribution: SalesEntryAttribution;
  readonly activity: {
    readonly newInquiries: number;
    readonly byScenario: readonly SalesCount[];
    readonly notRouted: number;
    readonly unrecognizedScenario: number;
    readonly byChannel: readonly SalesCount[];
    readonly firstResponse: {
      readonly answered: number;
      readonly unanswered: number;
      readonly unansweredWithReleasedDraft: number;
      readonly all: SalesSpread;
      readonly byOwner: SalesSpread;
      readonly byAgent: SalesSpread;
      /** Stage 1's definition, for the side-by-side with a corpus baseline. */
      readonly corpusDefinition: SalesSpread;
    };
    readonly contactAsk: {
      readonly transmitted: number;
      readonly holdingLines: number;
      readonly composed: number;
      /** Composed drafts whose text that left carries the fixed ask. The four below split it. */
      readonly carried: number;
      readonly appendedAtCompose: number;
      readonly addedAtApproval: number;
      /** Carried with both flags saying code added nothing: the model's own words, or the owner's edit. */
      readonly notAddedByCode: number;
      /** Carried, with no compose flag (a draft from before migration 0035) and no approval flag. */
      readonly howNotRecorded: number;
      /** Composed drafts whose text that left does not carry the fixed ask, whatever the flags say. */
      readonly without: number;
      readonly ownerReplies: number;
    };
    readonly scenarioMix: {
      readonly customerMessages: number;
      readonly routed: readonly SalesCount[];
      readonly notRouted: number;
      readonly unrecognized: number;
    };
  };
  readonly outcomes: {
    /**
     * Every label, zero-filled, in OUTCOMES order. There is no total across labels: these are the rows RECORDED
     * (a deposit and the sale after it are both rows here), not revenue.
     */
    readonly byLabel: Readonly<Record<Outcome, SalesLabelTally>>;
    /**
     * Revenue by the one rule (B2 note 3, revenue.ts `realizedRevenue`) over the outcomes recorded in the period:
     * a sale counts once at its own amount, a deposit followed by a sale on one thread is not added on top, a
     * deposit alone is deposit revenue, an unknown amount is counted apart and never as zero, a reversed row is
     * excluded. A deposit recorded before the period whose sale falls inside it is not seen as a pair here.
     */
    readonly revenue: RealizedRevenue;
    readonly reversed: Readonly<Record<Outcome, number>>;
    /** Unwindowed rows whose inquiry type is not recorded at all (TA-91). */
    readonly unwindowedInquiryTypeNotRecorded: number;
  };
}

function ascending(values: readonly number[]): number[] {
  return [...values].sort((a, b) => a - b);
}

/**
 * The upper middle, `sorted[floor(n / 2)]`, the way stage 4 takes the
 * baseline's `medianFirstResponseMinutes`, so a live median and a corpus one
 * are the same kind of number. Null on an empty set.
 */
export function salesMedian(values: readonly number[]): number | null {
  const sorted = ascending(values);
  return sorted[Math.floor(sorted.length / 2)] ?? null;
}

/** Nearest rank, as evals' canary takes a percentile: always a real sample. Null on an empty set. */
export function salesP90(values: readonly number[]): number | null {
  const sorted = ascending(values);
  return sorted[Math.max(Math.ceil(0.9 * sorted.length) - 1, 0)] ?? null;
}

function spread(values: readonly number[]): SalesSpread {
  return { count: values.length, medianMs: salesMedian(values), p90Ms: salesP90(values) };
}

function isScenario(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(SCENARIO_MAP, name);
}

/** Most first, then by name, so the listing never reorders between equal runs. */
function counted(keys: readonly string[]): SalesCount[] {
  const tally = new Map<string, number>();
  for (const k of keys) tally.set(k, (tally.get(k) ?? 0) + 1);
  return [...tally.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

function windowFor(inquiryType: string | null): number | null {
  if (inquiryType === null || !Object.prototype.hasOwnProperty.call(SALES_ATTRIBUTION_WINDOW_MS, inquiryType)) {
    return null;
  }
  return SALES_ATTRIBUTION_WINDOW_MS[inquiryType as InquiryType] ?? null;
}

/**
 * Assisted: an agent-composed draft on the same thread was transmitted
 * strictly before the outcome was recorded, and the outcome was recorded
 * strictly less than the window after it. Both edges are exclusive, the
 * strict side: an outcome stamped the same millisecond as the transmit did
 * not follow it, and one stamped exactly a window later is outside it.
 * campaignRoi.ts counts both of its edges in; this measure claims less.
 * A holding line is not composed, so it assists nothing.
 */
function isAssisted(o: SalesOutcomeRow, windowMs: number, transmits: readonly SalesTransmitRow[]): boolean {
  const recorded = o.recordedAt.getTime();
  let latest: number | null = null;
  for (const t of transmits) {
    if (t.threadId !== o.threadId || t.holdingLine === true) continue;
    const sent = t.transmittedAt.getTime();
    if (sent < recorded && (latest === null || sent > latest)) latest = sent;
  }
  return latest !== null && recorded - latest < windowMs;
}

interface MutableBucket {
  count: number;
  amountCents: number;
  withoutAmount: number;
}

function emptyBucket(): MutableBucket {
  return { count: 0, amountCents: 0, withoutAmount: 0 };
}

function add(bucket: MutableBucket, amountCents: number | null): void {
  bucket.count += 1;
  if (amountCents === null) bucket.withoutAmount += 1;
  else bucket.amountCents += amountCents;
}

/**
 * Per label, the unreversed rows' count and integer cents, one bucket per
 * label in OUTCOMES order and never a figure across labels (TA-504, TA-508).
 * The same `add` and the same two refusals `measureSales` applies to its
 * outcomes, exported so the weekly digest reads money through this arithmetic
 * and does not derive its own. A reversed row is excluded, as there.
 */
export function tallyOutcomeCents(
  outcomes: readonly Pick<SalesOutcomeRow, "outcomeId" | "label" | "amountCents" | "reversedAt">[],
): Readonly<Record<Outcome, SalesBucket>> {
  const byLabel = {} as Record<Outcome, MutableBucket>;
  for (const label of OUTCOMES) byLabel[label] = emptyBucket();
  for (const o of outcomes) {
    if (!(OUTCOMES as readonly string[]).includes(o.label)) {
      throw new Error(`outcome ${o.outcomeId} has a label this measure does not know`);
    }
    if (o.amountCents !== null && !Number.isSafeInteger(o.amountCents)) {
      throw new Error(`outcome ${o.outcomeId} has an amount that is not whole cents`);
    }
    if (o.reversedAt !== null) continue;
    add(byLabel[o.label], o.amountCents);
  }
  return byLabel;
}

interface MutableEntryTally {
  inquiries: number;
  viaContactComment: number;
  byLabel: Record<Outcome, MutableBucket>;
}

function emptyEntryTally(): MutableEntryTally {
  const byLabel = {} as Record<Outcome, MutableBucket>;
  for (const label of OUTCOMES) byLabel[label] = emptyBucket();
  return { inquiries: 0, viaContactComment: 0, byLabel };
}

function hasOwn(table: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(table, key);
}

type HeldBack = SalesContactCommentJoin["heldBack"];

/**
 * Sheet item 159. What a thread's entry source is, for a thread that began
 * with none of its own: the media id of the contact's latest comment before the
 * DM, when it came strictly before the DM's first message, less than
 * `SALES_COMMENT_LOOKBACK_MS` before it, and carried one; else why not. Only a
 * thread with no source anywhere is asked: a first message that carried one
 * keeps it, and a thread that learned one on a later message is counted apart
 * (`laterSource`), never filled in from another thread. It is the one nearest
 * comment that is asked and never an earlier one: a latest comment that is too
 * old, or carried no id, is not walked past to a better one.
 */
function contactCommentSource(i: SalesInquiryRow): { readonly source: string } | { readonly held: keyof HeldBack } {
  if (!hasOwn(SALES_COMMENT_CHANNEL_OF, i.channel)) return { held: "notADmThread" };
  const c = i.contactCommentThread;
  if (c === null) return { held: "noCommentThread" };
  const p = c.precedingComment;
  // Strictly before: at the same instant nothing says which came first, and a
  // comment that follows the DM did not produce it. The loader selects on this
  // too and core does not trust that.
  if (p === null || p.at.getTime() >= i.firstCustomerAt.getTime()) return { held: "noCommentBefore" };
  // Exclusive at the far edge, like the attribution windows.
  if (i.firstCustomerAt.getTime() - p.at.getTime() >= SALES_COMMENT_LOOKBACK_MS) return { held: "commentTooOld" };
  if (p.entrySource === null || p.entrySource === "") return { held: "commentCarriedNone" };
  return { source: p.entrySource };
}

function zeroByLabel(): Record<Outcome, number> {
  const out = {} as Record<Outcome, number>;
  for (const label of OUTCOMES) out[label] = 0;
  return out;
}

/**
 * H13. Runs after `measureSales` has refused any outcome with a label it does
 * not know or an amount that is not whole cents, so every row it adds is one
 * section 3 would also have counted.
 *
 * The cohort is the period's new inquiries, and an outcome belongs to a
 * source only when it was recorded on one of those threads. That keeps one
 * thread in one place, and the two counts that say what was left out
 * (`reversed`, `onOtherThreads`) keep this section reconcilable with section 3.
 */
function entryAttribution(input: SalesInput): SalesEntryAttribution {
  const bySource = new Map<string, MutableEntryTally>();
  const noSource = emptyEntryTally();
  const laterSource = emptyEntryTally();
  const unprintable = emptyEntryTally();
  const heldBack: { -readonly [K in keyof HeldBack]: number } = {
    noCommentThread: 0,
    noCommentBefore: 0,
    commentTooOld: 0,
    commentCarriedNone: 0,
    notADmThread: 0,
  };
  // Thread id -> the tally its inquiry was counted in.
  const home = new Map<string, MutableEntryTally>();
  for (const i of input.inquiries) {
    // An empty string is an absent source: parse.ts keeps whatever string the
    // payload held, and "" names no story.
    let source = i.entrySource === "" ? null : i.entrySource;
    let viaContact = false;
    let heldFor: keyof HeldBack | null = null;
    const threadHolds = i.threadEntrySource !== null && i.threadEntrySource !== "";
    if (source === null && !threadHolds) {
      // Sheet item 159: the one case a contact's comment thread may speak for.
      const read = contactCommentSource(i);
      if ("source" in read) {
        source = read.source;
        viaContact = true;
      } else {
        heldFor = read.held;
      }
    }
    let tally: MutableEntryTally;
    if (source === null) {
      // The first message named none. A source the thread holds anyway came
      // on a later message, and is counted apart rather than credited.
      tally = threadHolds ? laterSource : noSource;
      if (heldFor !== null) heldBack[heldFor] += 1;
    } else if (!ENTRY_SOURCE_ID.test(source)) {
      tally = unprintable;
    } else {
      tally = bySource.get(source) ?? emptyEntryTally();
      bySource.set(source, tally);
    }
    tally.inquiries += 1;
    if (viaContact) tally.viaContactComment += 1;
    home.set(i.threadId, tally);
  }

  const reversed = zeroByLabel();
  const onOtherThreads = zeroByLabel();
  for (const o of input.outcomes) {
    const tally = home.get(o.threadId);
    if (o.reversedAt !== null) {
      // On another thread it is section 3's reversed count and nothing here.
      if (tally !== undefined) reversed[o.label] += 1;
    } else if (tally === undefined) {
      onOtherThreads[o.label] += 1;
    } else {
      add(tally.byLabel[o.label], o.amountCents);
    }
  }

  // Plain code-unit order on the id, not the locale's: two machines with
  // different ICU data must list equal counts the same way.
  const ranked = [...bySource.entries()]
    .map(([entrySource, t]) => ({ entrySource, inquiries: t.inquiries, viaContactComment: t.viaContactComment, byLabel: t.byLabel }))
    .sort((a, b) => b.inquiries - a.inquiries || (a.entrySource < b.entrySource ? -1 : a.entrySource > b.entrySource ? 1 : 0));
  const rest = ranked.slice(SALES_ENTRY_SOURCE_CAP);
  const overflow = emptyEntryTally();
  for (const r of rest) {
    overflow.inquiries += r.inquiries;
    overflow.viaContactComment += r.viaContactComment;
    // The same label summed over sources, never one label into another.
    for (const label of OUTCOMES) {
      overflow.byLabel[label].count += r.byLabel[label].count;
      overflow.byLabel[label].amountCents += r.byLabel[label].amountCents;
      overflow.byLabel[label].withoutAmount += r.byLabel[label].withoutAmount;
    }
  }

  // Every thread is in exactly one tally, so section 4 adds up to section 1.
  // A sum that does not is a defect here, and a report that printed it would
  // read as a lead lost or counted twice.
  const accounted = [...bySource.values(), noSource, laterSource, unprintable].reduce((n, t) => n + t.inquiries, 0);
  if (accounted !== input.inquiries.length) {
    throw new Error(`entry attribution counted ${accounted} threads against ${input.inquiries.length} new inquiries`);
  }
  const credited = [...bySource.values(), unprintable].reduce((n, t) => n + t.viaContactComment, 0);
  return {
    cap: SALES_ENTRY_SOURCE_CAP,
    sources: ranked.slice(0, SALES_ENTRY_SOURCE_CAP),
    overflow: { sources: rest.length, ...overflow },
    noSource,
    laterSource,
    unprintable,
    contactComment: { credited, heldBack, accounted },
    reversed,
    onOtherThreads,
  };
}

export function measureSales(input: SalesInput): SalesMeasure {
  // Activity: new inquiries.
  const routed = input.inquiries.filter((i) => i.scenario !== null && isScenario(i.scenario));
  const answered = input.inquiries.filter((i) => i.firstResponse !== null);
  const unanswered = input.inquiries.filter((i) => i.firstResponse === null);
  const fromFirst = (by: "shop" | "agent" | null): number[] =>
    answered
      .filter((i) => by === null || i.firstResponse?.by === by)
      .map((i) => (i.firstResponse?.at.getTime() ?? 0) - i.firstCustomerAt.getTime());

  // Activity: the fixed contact ask on what the agent transmitted. Whether
  // from the text that left, how from the flags, so the four ways in sum to
  // the carried count and a flag never stands in for the text.
  const composed = input.transmittedInPeriod.filter((t) => t.holdingLine !== true);
  const carried = composed.filter((t) => t.carriesFixedAsk);
  const appendedAtCompose = carried.filter((t) => t.contactAskAppended === true).length;
  const addedAtApproval = carried.filter((t) => t.contactAskAppended !== true && t.contactAskAddedAtApproval).length;
  const notAddedByCode = carried.filter((t) => t.contactAskAppended === false && !t.contactAskAddedAtApproval).length;
  const howNotRecorded = carried.filter((t) => t.contactAskAppended === null && !t.contactAskAddedAtApproval).length;

  // Activity: the scenario mix over every customer message.
  const mixRouted = input.routedMessages.filter((m) => m.scenario !== null && isScenario(m.scenario));

  // Outcomes.
  const byLabel = {} as Record<
    Outcome,
    { all: MutableBucket; assisted: MutableBucket; ownerOnly: MutableBucket; unwindowed: MutableBucket; notRecorded: number }
  >;
  const reversed = {} as Record<Outcome, number>;
  for (const label of OUTCOMES) {
    byLabel[label] = { all: emptyBucket(), assisted: emptyBucket(), ownerOnly: emptyBucket(), unwindowed: emptyBucket(), notRecorded: 0 };
    reversed[label] = 0;
  }
  // Sheet item 146: the types the map names, read off the scenarios routed on
  // an outcome's own thread up to the moment it was recorded, and no further
  // back than `SALES_ROUTING_LOOKBACK_MS` (a thread is permanent, so an older
  // routing is another conversation). Computed here, per report, and never
  // written anywhere.
  const routingByThread = new Map<string, SalesRoutingRow[]>();
  for (const r of input.outcomeRouting) {
    const list = routingByThread.get(r.threadId) ?? [];
    list.push(r);
    routingByThread.set(r.threadId, list);
  }
  const basis = {} as Record<Outcome, { ownerStated: string[]; fromMap: string[]; ambiguous: number; noMappedScenario: number }>;
  for (const label of OUTCOMES) basis[label] = { ownerStated: [], fromMap: [], ambiguous: 0, noMappedScenario: 0 };
  let unwindowedInquiryTypeNotRecorded = 0;
  for (const o of input.outcomes) {
    if (!(OUTCOMES as readonly string[]).includes(o.label)) {
      throw new Error(`outcome ${o.outcomeId} has a label this measure does not know`);
    }
    if (o.amountCents !== null && !Number.isSafeInteger(o.amountCents)) {
      throw new Error(`outcome ${o.outcomeId} has an amount that is not whole cents`);
    }
    if (o.reversedAt !== null) {
      reversed[o.label] += 1;
      continue;
    }
    const tally = byLabel[o.label];
    add(tally.all, o.amountCents);
    let inquiryType: string | null = o.inquiryType;
    if (inquiryType !== null) {
      basis[o.label].ownerStated.push(inquiryType);
    } else {
      const named = new Set<string>();
      const recorded = o.recordedAt.getTime();
      for (const r of routingByThread.get(o.threadId) ?? []) {
        if (r.scenario === null || !hasOwn(SALES_INQUIRY_TYPE_OF_SCENARIO, r.scenario)) continue;
        const sent = r.sentAt.getTime();
        // After the outcome is another conversation; the lookback's old edge is exclusive.
        if (sent > recorded || recorded - sent >= SALES_ROUTING_LOOKBACK_MS) continue;
        named.add(SALES_INQUIRY_TYPE_OF_SCENARIO[r.scenario] as string);
      }
      if (named.size === 1) {
        inquiryType = [...named][0] ?? null;
        basis[o.label].fromMap.push(inquiryType ?? "");
      } else if (named.size > 1) {
        basis[o.label].ambiguous += 1;
      } else {
        basis[o.label].noMappedScenario += 1;
      }
    }
    const windowMs = windowFor(inquiryType);
    if (windowMs === null) {
      add(tally.unwindowed, o.amountCents);
      if (inquiryType === null) {
        tally.notRecorded += 1;
        unwindowedInquiryTypeNotRecorded += 1;
      }
    } else if (isAssisted(o, windowMs, input.transmits)) {
      add(tally.assisted, o.amountCents);
    } else {
      add(tally.ownerOnly, o.amountCents);
    }
  }
  const labelTallies = {} as Record<Outcome, SalesLabelTally>;
  for (const label of OUTCOMES) {
    const t = byLabel[label];
    const b = basis[label];
    labelTallies[label] = {
      ...t.all,
      assisted: t.assisted,
      ownerOnly: t.ownerOnly,
      unwindowed: t.unwindowed,
      windowed: t.assisted.count + t.ownerOnly.count,
      inquiryTypeNotRecorded: t.notRecorded,
      inquiryTypeBasis: {
        ownerStated: counted(b.ownerStated),
        fromMap: counted(b.fromMap),
        ambiguous: b.ambiguous,
        noMappedScenario: b.noMappedScenario,
      },
    };
  }

  return {
    entryAttribution: entryAttribution(input),
    activity: {
      newInquiries: input.inquiries.length,
      byScenario: counted(routed.map((i) => i.scenario ?? "")),
      notRouted: input.inquiries.filter((i) => i.scenario === null).length,
      unrecognizedScenario: input.inquiries.filter((i) => i.scenario !== null && !isScenario(i.scenario)).length,
      byChannel: counted(input.inquiries.map((i) => i.channel)),
      firstResponse: {
        answered: answered.length,
        unanswered: unanswered.length,
        unansweredWithReleasedDraft: unanswered.filter((i) => i.releasedNotTransmitted).length,
        all: spread(fromFirst(null)),
        byOwner: spread(fromFirst("shop")),
        byAgent: spread(fromFirst("agent")),
        corpusDefinition: spread(
          answered.map((i) => (i.firstResponse?.at.getTime() ?? 0) - (i.firstResponse?.lastCustomerAt.getTime() ?? 0)),
        ),
      },
      contactAsk: {
        transmitted: input.transmittedInPeriod.length,
        holdingLines: input.transmittedInPeriod.length - composed.length,
        composed: composed.length,
        carried: carried.length,
        appendedAtCompose,
        addedAtApproval,
        notAddedByCode,
        howNotRecorded,
        without: composed.length - carried.length,
        ownerReplies: input.ownerRepliesInPeriod,
      },
      scenarioMix: {
        customerMessages: input.routedMessages.length,
        routed: counted(mixRouted.map((m) => m.scenario ?? "")),
        notRouted: input.routedMessages.filter((m) => m.scenario === null).length,
        unrecognized: input.routedMessages.filter((m) => m.scenario !== null && !isScenario(m.scenario)).length,
      },
    },
    outcomes: {
      byLabel: labelTallies,
      revenue: realizedRevenue(
        input.outcomes.map((o) => ({
          outcomeId: o.outcomeId,
          threadId: o.threadId,
          label: o.label,
          amountCents: o.amountCents,
          amountStatus: o.amountStatus,
          recordedAt: o.recordedAt,
          reversedAt: o.reversedAt,
        })),
      ),
      reversed,
      unwindowedInquiryTypeNotRecorded,
    },
  };
}
